/**
 * objections.ts — the objection helper library (GUI v2 feature 6).
 *
 * Source of truth: 13_objection-handling-playbook.md — all 29 objections, each
 * following the guide's five-step pattern (acknowledge, clarify, educate,
 * reconnect, ask/advance). Two extra cards (spouse approval, "think about it")
 * are sourced from 03_stage-by-stage-advisor-guide.md § Stage 10 strong phrases,
 * because file 13 has no dedicated entries for them — noted for Phil in the
 * manifest. status "pending" = the underlying script sits on a TBD / framework
 * slot / DRAFT-FOR-OWNER-REVIEW item (renders with a pending badge, req G.7).
 *
 * QUICK_ACCESS_IDS (req 6.2): the always-visible manual row. File 13 does not
 * flag frequency, so the default set is PROPOSED (manager-configurable later
 * via tenant_config) — flagged for Phil in the manifest.
 */

export interface ObjectionDef {
  id: string;
  /** ≤20-char chip label for the quick-access row. */
  label: string;
  category: "price" | "trust" | "timing" | "authority" | "product" | "legal" | "process";
  headline: string;
  /** The single best verbatim line. */
  verbatim: string;
  steps: {
    acknowledge: string;
    clarify: string;
    educate: string;
    reconnect: string;
    advance: string;
  };
  products: ("tint" | "ppf" | "coating")[];
  status: "ok" | "pending";
  source: string;
}

export const OBJECTIONS: ObjectionDef[] = [
  {
    id: "thats-expensive",
    label: "Price",
    category: "price",
    headline: "Connect price to value — never argue",
    verbatim:
      "The price difference between shops is almost always a difference in film, coverage, and warranty, not just markup.",
    steps: {
      acknowledge: "Sounds like that's more than you expected. That's fair, and I'm glad you said it.",
      clarify: "Did you have a number in mind, or have you gotten another quote somewhere?",
      educate:
        "Central Indiana exclusive authorized autobahn installer; lifetime manufacturer warranty plus no-fault Life Happens coverage.",
      reconnect: "You told me the big thing was [their goal] — that's exactly what this package is built for.",
      advance:
        "Synchrony, 6–12 months no interest for qualified applicants. Want me to grab you a spot this week?",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §1",
  },
  {
    id: "cheaper-tint-quote",
    label: "Cheaper quote",
    category: "price",
    headline: "Same name, different jobs — no price match",
    verbatim: "Do you know if they quoted the same coverage areas and film quality?",
    steps: {
      acknowledge: "That makes sense. You're comparing a few options, and that's smart.",
      clarify: "Did their quote include all the sides and the rear glass, and which film line was it?",
      educate:
        "Film line, coverage, and warranty differ a lot; we're the exclusive autobahn installer. We don't price match.",
      reconnect: "You mentioned [their goal] got you shopping — which quote actually solves that?",
      advance:
        "Come feel the heat rejection difference yourself and leave with a 100% accurate quote. Indianapolis or Greenwood?",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §2",
  },
  {
    id: "regular-vs-ceramic",
    label: "Why ceramic?",
    category: "product",
    headline: "Same darkness, very different comfort",
    verbatim: "Same darkness, very different comfort.",
    steps: {
      acknowledge: "Great question — honestly the most important one, because this is where quotes really differ.",
      clarify: "What's driving the tint for you — mostly looks and privacy, or is heat a big part of it?",
      educate: "IRR: Black 13%, Black Ceramic 60%, i3 93%, i3+ 99%. TSER up to 43/63/71/74% depending on shade.",
      reconnect: "If heat matters, look hard at i3 — that 60-to-93 jump in infrared rejection is something you feel.",
      advance: "Come feel the showroom heat demo and leave with a 100% accurate quote. Indianapolis or Greenwood?",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §3",
  },
  {
    id: "cheapest-tint",
    label: "Cheapest",
    category: "price",
    headline: "One education moment, then respect the budget",
    verbatim: "If the budget is firm, Black is a film we're proud to put on your car, and I'm happy to book it.",
    steps: {
      acknowledge: "Totally fine — our entry film is a really solid product.",
      clarify: "Is this mostly about the look and privacy, or is the heat in the car part of why you're calling?",
      educate:
        "Ceramic blocks up to 63–74% of total solar energy. Black Ceramic $509; Black $349 with the same lifetime warranty + Life Happens.",
      reconnect: "If the cooler-cabin difference sounds worth $160, Black Ceramic is where I'd start. Which way feels right?",
      advance: "Want me to get you on the schedule? I've got [day] or [day] open.",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §4",
  },
  {
    id: "ceramic-skepticism",
    label: "Really works?",
    category: "trust",
    headline: "Give the numbers, then the heat demo",
    verbatim: "Don't take my word for it — put your hand behind each film on our heat demo. You'll know in ten seconds.",
    steps: {
      acknowledge: "Fair question. A lot of upgrades out there are marketing, so I get why you'd ask.",
      clarify: "What would make the difference for you — heat through the glass, glare, or something else?",
      educate: "Measurable: IRR 13% (Black) → 60 (Black Ceramic) → 93 (i3) → 99 (i3+). Manufacturer spec sheets.",
      reconnect: "That jump from 13 to 93 percent infrared rejection is exactly the difference you'd be paying for.",
      advance: "Come by the showroom, feel the demo, leave with a 100% accurate quote. Indianapolis or Greenwood?",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §5",
  },
  {
    id: "legal-tint-limit",
    label: "Legal limit",
    category: "legal",
    headline: "Official guidance only — never improvise",
    verbatim:
      "Front sides 30 percent. Rear sides 30 on cars; SUVs, vans, trucks can go to 5. No windshield tint; brow at 5.",
    steps: {
      acknowledge: "Good question — glad you asked before booking instead of after.",
      clarify: "What are you driving? The guidance differs for cars versus SUVs, vans, and trucks.",
      educate:
        "Indiana guidance: fronts 30%. Rear sides 30% cars, 5% SUV/van/truck. No windshield tint; brow 5%. Medical exemption: prescription on file.",
      reconnect: "Based on what you're driving and [their goal], that gives you [the applicable options].",
      advance: "Come see the shades side by side on glass and leave with a 100% accurate quote. Want me to set that up?",
    },
    products: ["tint"],
    status: "pending",
    source: "13 §6 (legal phrasing DRAFT FOR OWNER REVIEW)",
  },
  {
    id: "darker-than-legal",
    label: "Go darker",
    category: "legal",
    headline: "Sides/rear at request; windshield 40% floor",
    verbatim:
      "We'll install any shade on sides and rear at your request — the legal responsibility is yours, and the warranty may be waived.",
    steps: {
      acknowledge: "Yeah, plenty of people love that look, and I get it.",
      clarify: "Just so I quote it right — sides and rear, or were you hoping to do the windshield too?",
      educate:
        "Darker than legal: you take on the legal responsibility, and the warranty may be waived. Windshield: never darker than 40%, firm.",
      reconnect: "We can get you there on the sides and rear — eyes open on those two points.",
      advance: "Want me to price it both ways, the legal shade and the darker one?",
    },
    products: ["tint"],
    status: "pending",
    source: "13 §7 (DRAFT FOR OWNER REVIEW)",
  },
  {
    id: "heat-ac-help",
    label: "Will it cool?",
    category: "product",
    headline: "Less heat in, less A/C work",
    verbatim: "Ceramic i3 rejects 93 percent of infrared — the heat you feel on your skin — and i3+ rejects 99 percent.",
    steps: {
      acknowledge: "That's the number one reason people call us, so you're asking the right question.",
      clarify: "Is it the car baking while parked, the sun on your arm while driving, or both?",
      educate: "i3 rejects 93% of infrared, i3+ 99%; TSER up to 71%/74% depending on shade. Less heat in, A/C works less.",
      reconnect: "For [their heat complaint], that's exactly the problem the ceramic lines exist to solve.",
      advance: "Fastest way to believe it is to feel it — showroom heat demo, 100% accurate quote. This week?",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §8",
  },
  {
    id: "fuel-savings",
    label: "Save gas?",
    category: "product",
    headline: "Yes as a bonus — no figures, no promises",
    verbatim:
      "Yes, running the A/C less does save fuel, especially in summer — but I can't give you an exact number or promise it pays for itself.",
    steps: {
      acknowledge: "Your logic is right, and with fuel prices where they are, a lot of people think the same way.",
      clarify: "Sounds like you're looking for the practical payoff, not just the look. Is that fair?",
      educate: "Less heat in, A/C works less, uses less fuel — true. No exact numbers, no pays-for-itself promise.",
      reconnect: "The main benefit is the one you feel every day — cooler car, cools faster. Fuel is a nice bonus.",
      advance: "Want me to put together the right package for your vehicle and get you on the schedule?",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §9",
  },
  {
    id: "install-time",
    label: "How long?",
    category: "process",
    headline: "In by 11, done same day — no clock promises",
    verbatim: "Get the car to us by 11:00 am and it's done the same day, and we call you the moment it's finished.",
    steps: {
      acknowledge: "Good question — let me give you the honest picture so you can plan your day.",
      clarify: "Were you hoping to wait with the car, or would dropping it off work?",
      educate: "Drop-off works best: in by 11:00 am, done same day. Waiting possible on smaller jobs, no guaranteed window.",
      reconnect: "Since you mentioned [their schedule], drop-off means you plan your morning and we handle the rest.",
      advance: "Want me to grab you a morning slot? I've got [day] and [day] open.",
    },
    products: ["tint"],
    status: "pending",
    source: "13 §10 ([TBD: tint install durations])",
  },
  {
    id: "appointment-needed",
    label: "Walk in?",
    category: "process",
    headline: "$100 deposit locks the slot; walk-ins OK",
    verbatim: "The $100 deposit applies straight toward your total, and it locks your slot.",
    steps: {
      acknowledge: "You can do either, so let me lay out both.",
      clarify: "Are you hoping to get it done today, or just planning ahead?",
      educate: "Appointment + $100 deposit (applies to total) guarantees the spot. Walk-ins welcome when there's room.",
      reconnect: "Since you said [their timing], the appointment means you know exactly when it's handled.",
      advance: "I can lock you in right now. Would [day] or [day] work better?",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §11",
  },
  {
    id: "discount-ask",
    label: "Discount",
    category: "price",
    headline: "Trade value, never just cut price",
    verbatim: "Our prices are set, but I can take the Life Happens coverage off and get the number down. The trade is real.",
    steps: {
      acknowledge: "Here's how I can work with you on that.",
      clarify: "Is getting the number down the priority, even if the no-fault coverage comes off?",
      educate: "Prices are set; a lower price means the film isn't covered the same way later.",
      reconnect: "Most folks, when they hear that, keep the warranty — the part you'll actually be glad you have.",
      advance: "Which way do you want to go? (If they keep it: \"Smart call. Let's book the full package.\")",
    },
    products: ["tint"],
    status: "ok",
    source: "13_objection-handling-playbook.md §12 (the value trade)",
  },
  {
    id: "think-about-it",
    label: "Think about it",
    category: "timing",
    headline: "Find what they want to think over",
    verbatim: "Totally understand. What specifically do you want to think over? I can make sure you have those answers.",
    steps: {
      acknowledge: "Totally understand wanting to sit with it.",
      clarify: "What's the main thing you want to think over — the price, the shade, the timing?",
      educate: "Whatever it is, I'd rather you have the real answer now than guess at it later.",
      reconnect: "You called because [their goal] — the quote I'm sending covers exactly that.",
      advance: "Here's what I'll do either way: I'm texting you the full quote, and I'll check back Friday. Does Friday work?",
    },
    products: ["tint", "ppf", "coating"],
    status: "ok",
    source: "03_stage-by-stage-advisor-guide.md § Stage 10 (no dedicated entry in file 13)",
  },
  {
    id: "spouse-approval",
    label: "Spouse",
    category: "authority",
    headline: "Arm them for the conversation at home",
    verbatim: "Totally understand wanting to talk it over. What do you think she'll want to know?",
    steps: {
      acknowledge: "Totally understand — that's a real conversation, not a brush-off.",
      clarify: "What do you think they'll want to know? I can make sure you have those answers.",
      educate: "The quote in writing covers film, coverage, and the warranty — the three things people ask about.",
      reconnect: "You called because [their goal]; the quote shows exactly what fixes it and what it costs.",
      advance: "I'll text the written quote now and check in [day]. If it's a green light sooner, the link books it.",
    },
    products: ["tint", "ppf", "coating"],
    status: "ok",
    source: "03_stage-by-stage-advisor-guide.md § Stage 10 (no dedicated entry in file 13)",
  },
  {
    id: "ppf-price",
    label: "PPF price",
    category: "price",
    headline: "Material + skilled hands; price at consult",
    verbatim: "Paint damage is permanent, and PPF is the one product that prevents it instead of treating it.",
    steps: {
      acknowledge: "Sounds like that's more than you expected — normal the first time someone prices PPF.",
      clarify: "Can I ask what you're comparing it to? Another PPF quote, or just the number itself?",
      educate: "Thick urethane film takes the hit so your paint doesn't. Cost = material + skilled hand labor.",
      reconnect: "You want to keep the new car looking new. Rock chips can't be undone short of repainting.",
      advance: "The right way to price this is in person — precise quote at the consult. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "pending",
    source: "13_objection-handling-playbook.md §13",
  },
  {
    id: "full-front-coverage",
    label: "Full front?",
    category: "product",
    headline: "Depends on your driving — consult confirms",
    verbatim: "Coverage levels exist so you protect where your actual risk is, not more and not less.",
    steps: {
      acknowledge: "That's the right question, and the honest answer is: it depends on your driving.",
      clarify: "How much highway time does this car see? Parked outside or garaged?",
      educate: "Damage concentrates where the road throws debris — bumper, front of hood, fenders, mirrors.",
      reconnect: "What you told me about [their driving] points toward [more or less coverage].",
      advance: "We look the vehicle over together and you leave with a precise quote. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "pending",
    source: "13_objection-handling-playbook.md §14",
  },
  {
    id: "bumper-only",
    label: "Bumper only",
    category: "product",
    headline: "Real protection with a boundary — take the yes",
    verbatim: "Bumper-only is real protection, it just has a boundary.",
    steps: {
      acknowledge: "Absolutely — the bumper takes the most abuse, so it's a sensible place to start.",
      clarify: "Is that a budget call, or do you figure the bumper is the only part at risk?",
      educate: "Chips don't respect panel lines — the hood's leading edge and fenders catch plenty too.",
      reconnect: "Starting with the bumper protects the highest risk, and we can add coverage later.",
      advance: "Let's get you in for a consult — see what each coverage level protects. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "pending",
    source: "13_objection-handling-playbook.md §15",
  },
  {
    id: "coating-vs-ppf",
    label: "Coating = PPF?",
    category: "product",
    headline: "Different products doing different jobs",
    verbatim: "PPF absorbs impact — it's the product for rock chips. Coating is gloss, easier cleaning, and maintenance.",
    steps: {
      acknowledge: "Really common question — the two get mixed up constantly, so I'm glad you asked.",
      clarify: "Main thing you're protecting against: rock chips and physical damage, or gloss and easy cleaning?",
      educate: "PPF is a physical film with real thickness that absorbs impact. Coating does not stop chips.",
      reconnect: "Since you said [their concern], that points you toward [PPF / coating / both].",
      advance: "Easiest way to sort it out is in person — precise quote for the right product. Thursday or Friday?",
    },
    products: ["ppf", "coating"],
    status: "ok",
    source: "13_objection-handling-playbook.md §16",
  },
  {
    id: "ppf-edges",
    label: "See the film?",
    category: "product",
    headline: "Honest: not invisible, barely findable",
    verbatim: "On a finished install most people cannot tell the film is there from a normal viewing distance.",
    steps: {
      acknowledge: "Honest question, and I'll give you an honest answer.",
      clarify: "Have you seen PPF on a car up close before, or would this be your first time?",
      educate: "Not invisible — if you know where to look you can find an edge. Patterning places edges along panel lines.",
      reconnect: "Does a barely-findable edge beat a permanent rock chip? For most owners that's easy.",
      advance: "Best way to judge is your own eyes — come look at film on actual paint. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "ok",
    source: "13_objection-handling-playbook.md §17",
  },
  {
    id: "ppf-lifespan",
    label: "PPF lifespan",
    category: "product",
    headline: "No invented numbers — lifespan at consult",
    verbatim: "The lifespan and warranty term for our film will be confirmed at your consult along with exact coverage.",
    steps: {
      acknowledge: "Good question — exactly the right way to think about an investment like this.",
      clarify: "How long are you planning to keep the vehicle?",
      educate: "Lifespan and warranty term are confirmed at your consult. [Pending package definition]",
      reconnect: "Given you're keeping the car [their timeframe], that math is what we'll walk through together.",
      advance: "Come in for the consult — precise quote and the full warranty picture. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "pending",
    source: "13_objection-handling-playbook.md §18",
  },
  {
    id: "ppf-removal",
    label: "Removable?",
    category: "product",
    headline: "Yes — film is the reversible option",
    verbatim: "Film is the reversible option, bare paint damage is the irreversible one.",
    steps: {
      acknowledge: "Yes, and that's actually one of the best things about film.",
      clarify: "Are you thinking about resale down the road, or just wanting to know you're not stuck?",
      educate: "Professionally removed, the paint underneath has been protected the whole time.",
      reconnect: "For resale especially, protected original paint is exactly what you want to show a buyer.",
      advance: "Want to come in and see it on actual paint? You'll leave with a precise quote. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "ok",
    source: "13_objection-handling-playbook.md §19",
  },
  {
    id: "ppf-film-damage",
    label: "Film damage",
    category: "product",
    headline: "Film taking the hit is it working",
    verbatim: "The film taking damage so the paint doesn't is it working, not failing.",
    steps: {
      acknowledge: "Smart thing to ask before you buy, not after.",
      clarify: "Are you picturing normal road wear, or something bigger like an accident?",
      educate: "A damaged section can be replaced as a section. Exact warranty terms in writing at the consult.",
      reconnect: "The math you care about stays intact: film takes the hit, paint stays perfect.",
      advance: "Come in for the consult — warranty terms in front of you plus the precise quote. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "pending",
    source: "13_objection-handling-playbook.md §20",
  },
  {
    id: "wait-for-chips",
    label: "Wait + see",
    category: "timing",
    headline: "Paint damage only moves in one direction",
    verbatim: "Waiting for more damage before protecting the paint is like waiting for a few more leaks before fixing the roof.",
    steps: {
      acknowledge: "I get the logic — why protect something that's already taken a hit.",
      clarify: "How many chips are we talking, a couple of small ones or real damage already?",
      educate: "Every chip your car has today, it keeps forever. Film stops every chip that hasn't happened yet.",
      reconnect: "The chips it has now are the floor. Film keeps the floor from dropping.",
      advance: "Bring it in — we'll look at the existing chips together. Thursday or Friday?",
    },
    products: ["ppf"],
    status: "ok",
    source: "13_objection-handling-playbook.md §21",
  },
  {
    id: "coating-worth-it",
    label: "Worth it?",
    category: "product",
    headline: "Gloss, easier cleaning, maintenance",
    verbatim: "If you wash your own car and care how it looks, coating earns its keep every single wash.",
    steps: {
      acknowledge: "Fair question, because coating gets both oversold and overdoubted out there.",
      clarify: "What would make it worth it for you — the look, easier washing, or protecting the finish?",
      educate: "Does: deep gloss, easier cleaning, maintenance. Doesn't: stop rock chips or wash itself.",
      reconnect: "Based on [their goal], coating is [a strong fit / worth weighing against PPF].",
      advance: "The recommendation depends on your paint's condition — quick inspection, precise quote. This week?",
    },
    products: ["coating"],
    status: "pending",
    source: "13_objection-handling-playbook.md §22",
  },
  {
    id: "coating-scratches",
    label: "Scratch-proof?",
    category: "product",
    headline: "Resists light marring — never scratch-proof",
    verbatim: "It will not stop a key, a branch, or a shopping cart. Nothing in a bottle will.",
    steps: {
      acknowledge: "Good question — coating gets overhyped on this exact point, so let me be straight.",
      clarify: "What kind of scratches — wash swirls and light marring, or deeper stuff like keys?",
      educate: "Coating can resist fine wash swirls. It won't stop real force — film absorbs impact.",
      reconnect: "For what you described, the honest fit is [coating / film / both].",
      advance: "Let's look at your paint together — quick inspection, precise quote. This week?",
    },
    products: ["coating"],
    status: "ok",
    source: "13_objection-handling-playbook.md §23",
  },
  {
    id: "coating-rock-chips",
    label: "Chips?",
    category: "product",
    headline: "Required script: coating is not PPF",
    verbatim: "I want to be clear: coating does not replace PPF for rock chips. If chips are the concern, look at film.",
    steps: {
      acknowledge: "That's a great question.",
      clarify: "Is highway debris the main thing you're trying to protect against?",
      educate: "Required script: coating does not replace PPF for rock chips. Film has real thickness; coating is microns.",
      reconnect: "Since chips are your concern, film is the honest recommendation.",
      advance: "Want to come look at both on real paint? Precise quote at the consult. This week?",
    },
    products: ["coating", "ppf"],
    status: "ok",
    source: "13_objection-handling-playbook.md §24",
  },
  {
    id: "coating-lifespan",
    label: "Coat lifespan",
    category: "product",
    headline: "No invented numbers — confirmed at inspection",
    verbatim: "We'll confirm the durability and warranty terms for the specific coating package at your inspection, in writing.",
    steps: {
      acknowledge: "You've probably heard every number under the sun — exactly why you're asking.",
      clarify: "How is the car kept — garaged or outside — and do you wash it yourself?",
      educate: "Durability depends on product tier and how the car lives. Terms confirmed in writing at inspection.",
      reconnect: "Given the car [lives outside], that's exactly what we'll weigh when we recommend a tier.",
      advance: "Let's get the inspection on the books — precise quote. This week good?",
    },
    products: ["coating"],
    status: "pending",
    source: "13_objection-handling-playbook.md §25",
  },
  {
    id: "still-wash-car",
    label: "Still wash?",
    category: "product",
    headline: "Easier, not optional",
    verbatim: "Yes, you still wash the car. What changes is how easy it is. Easier, not optional.",
    steps: {
      acknowledge: "Ha — I wish I could tell you yes. You'd be our favorite customer.",
      clarify: "How often do you wash it now, and yourself or through a wash?",
      educate: "Dirt and bugs don't bond to coated paint like bare clear coat — washes get faster.",
      reconnect: "You said you hate how long washes take — that's exactly the pain coating fixes.",
      advance: "Want to set up the inspection? Right package for your paint, precise quote.",
    },
    products: ["coating"],
    status: "ok",
    source: "13_objection-handling-playbook.md §26",
  },
  {
    id: "paint-correction",
    label: "Why correct?",
    category: "product",
    headline: "Coating locks in whatever is under it",
    verbatim: "It's the difference between laminating a clean page and laminating a smudged one.",
    steps: {
      acknowledge: "Totally fair question, because from the outside it can look like an add-on.",
      clarify: "Has the car been machine polished before, or is the paint as it came?",
      educate: "Coating seals in whatever's underneath — swirls and haze get locked under glass.",
      reconnect: "You're doing this for the gloss. Correction is what makes the gloss worth locking in.",
      advance: "Some cars need very little — bring it by, precise quote. This week?",
    },
    products: ["coating"],
    status: "ok",
    source: "13_objection-handling-playbook.md §27",
  },
  {
    id: "wax-instead",
    label: "Just wax?",
    category: "product",
    headline: "Recurring effort vs one pro application",
    verbatim: "The comparison is really: recurring effort forever, or one professional application that holds.",
    steps: {
      acknowledge: "You absolutely can — a regularly waxed car is a well cared for car.",
      clarify: "How often do you realistically get a coat of wax on it now?",
      educate: "Wax lasts weeks, maybe a couple months. Coating chemically bonds and lasts on a different timescale.",
      reconnect: "If the goal is the result without the recurring weekend project, coating wins that trade.",
      advance: "Bring it by — we'll see what the paint needs, and you'll leave with a precise quote.",
    },
    products: ["coating"],
    status: "ok",
    source: "13_objection-handling-playbook.md §28",
  },
  {
    id: "coating-price-detailer",
    label: "Detailer cheaper",
    category: "price",
    headline: "Prep, product, warranty move the price",
    verbatim: "Two quotes with the word ceramic in them can be completely different jobs.",
    steps: {
      acknowledge: "That makes sense — comparing a few options is the right move.",
      clarify: "Do you know what their price includes — correction, which product line, what warranty?",
      educate: "Price swings on three things: correction prep, the product grade, and the warranty behind it.",
      reconnect: "The question for both shops is what's done to the paint before anything gets locked in on top.",
      advance: "Bring the car by — leave with a precise quote you can compare line for line. This week?",
    },
    products: ["coating"],
    status: "pending",
    source: "13_objection-handling-playbook.md §29",
  },
];

/** Proposed default quick-access row (req 6.2) — manager-configurable later. */
export const QUICK_ACCESS_IDS: string[] = [
  "thats-expensive",
  "cheaper-tint-quote",
  "think-about-it",
  "spouse-approval",
  "regular-vs-ceramic",
  "appointment-needed",
];

export function objection(id: string): ObjectionDef | null {
  return OBJECTIONS.find((o) => o.id === id) ?? null;
}
